/**
 * @fileoverview Project submission success page with one-time access protection
 * @module routes/project-submitted
 */

import type { ReactElement } from 'react';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { DealsBackgroundShape } from '@/features/deals/components/DealsBackgroundShape';
import { BackButton } from '@/components/ui/BackButton';

/**
 * Service for managing project submission access control.
 *
 * Implements one-time access pattern using session storage.
 * Access is granted only immediately after form submission.
 */
class SubmissionAccessService {
  private static readonly ACCESS_KEY = 'project_submission_accessed';
  private static readonly TIMESTAMP_KEY = 'project_submission_timestamp';
  private static readonly ACCESS_WINDOW_MS = 5 * 60 * 1000; // 5 minutes

  /**
   * Grant access to the submission page.
   * Called immediately after successful form submission.
   */
  static grantAccess(): void {
    if (typeof window !== 'undefined') {
      sessionStorage.setItem(this.ACCESS_KEY, 'true');
      sessionStorage.setItem(this.TIMESTAMP_KEY, Date.now().toString());
    }
  }

  /**
   * Check if user has valid access to the submission page.
   * Access expires after the time window or page refresh.
   */
  static hasValidAccess(): boolean {
    if (typeof window === 'undefined') return false;

    const hasAccess = sessionStorage.getItem(this.ACCESS_KEY) === 'true';
    const timestamp = sessionStorage.getItem(this.TIMESTAMP_KEY);

    if (!hasAccess || !timestamp) return false;

    // Check if access is within the time window
    const accessTime = parseInt(timestamp, 10);
    const now = Date.now();
    const isWithinWindow = (now - accessTime) < this.ACCESS_WINDOW_MS;

    return isWithinWindow;
  }

  /**
   * Revoke access to the submission page.
   * Called after the page is viewed to prevent subsequent access.
   */
  static revokeAccess(): void {
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem(this.ACCESS_KEY);
      sessionStorage.removeItem(this.TIMESTAMP_KEY);
    }
  }
}

/**
 * Project submission success page component with one-time access protection.
 *
 * Features:
 * - One-time access control after form submission
 * - Automatic redirect for unauthorized access
 * - Background lighting shape from deals page
 * - Green title and card styling
 * - Custom card with corner borders
 * - Submission illustration
 * - Glowing shape under image
 * - Detailed success message
 *
 * Security:
 * - Only accessible immediately after form submission
 * - Access expires after 5 minutes or page refresh
 * - Redirects unauthorized users to home page
 */
function ProjectSubmittedPage(): ReactElement {
  const router = useRouter();
  const [isAuthorized, setIsAuthorized] = useState<boolean>(false);
  const [isChecking, setIsChecking] = useState<boolean>(true);

  /**
   * Verify access authorization on component mount.
   * Redirects unauthorized users to home page.
   */
  useEffect(() => {
    const checkAccess = (): void => {
      const hasAccess = SubmissionAccessService.hasValidAccess();

      if (hasAccess) {
        setIsAuthorized(true);
        // Access granted - DO NOT revoke yet, wait for navigation away
      } else {
        // Redirect unauthorized users to home page
        router.navigate({ to: '/', replace: true });
        return;
      }

      setIsChecking(false);
    };

    checkAccess();
  }, [router]);

  /**
   * Revoke access when component unmounts (user navigates away).
   * This ensures the page is only accessible once per session.
   * Includes a delay to prevent premature access revocation during rapid mounts/unmounts.
   */
  useEffect(() => {
    let hasBeenVisible = false;
    let visibilityTimer: NodeJS.Timeout;

    // Mark as visible after a brief delay to prevent rapid mount/unmount issues
    visibilityTimer = setTimeout(() => {
      hasBeenVisible = true;
    }, 1000);

    return () => {
      clearTimeout(visibilityTimer);

      // Only revoke access if the page was actually visible for some time
      if (hasBeenVisible) {
        SubmissionAccessService.revokeAccess();
      } else {
      }
    };
  }, []);

  // Show loading state while checking authorization
  if (isChecking) {
    return (
      <div className="relative min-h-screen bg-black overflow-hidden flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  // Don't render content for unauthorized users (they should be redirected)
  if (!isAuthorized) {
    return (
      <div className="relative min-h-screen bg-black overflow-hidden flex items-center justify-center">
        <div className="text-white">Redirecting...</div>
      </div>
    );
  }
  return (
    <div className="relative min-h-screen bg-black overflow-hidden flex flex-col">
      {/* Background Shape */}
      <DealsBackgroundShape />

      {/* Main Content */}
      <div className="relative z-10 w-full flex flex-col flex-1">
        {/* Header Section - Responsive */}
        <header className="text-center mb-10 md:mb-[60px] pt-20 md:pt-[140px] px-4 md:px-0">
          <h1
            className="font-bold mb-2 font-sans relative z-10 text-[28px] md:text-[36px] leading-[32px] md:leading-[40px]"
            style={{
              color: '#44FF02',
            }}
          >
            Project Submitted Successfully!
          </h1>
        </header>

        {/* Border Section - Desktop only */}
        <div className="hidden md:block max-w-[1300px] mx-auto relative">
          {/* Left vertical border extending upward to frame title */}
          <div
            className="absolute w-px bg-onicorn-gray-500 z-40"
            style={{
              top: 'calc(-12rem)',
              bottom: 'calc(1rem)',
              left: 'calc(50% - 225px - 110px)'
            }}
          />

          {/* Right vertical border extending upward to frame title */}
          <div
            className="absolute w-px bg-onicorn-gray-500 z-40"
            style={{
              top: 'calc(-12rem)',
              bottom: 'calc(1rem)',
              right: 'calc(50% - 225px - 110px)'
            }}
          />

          {/* Bottom border under title area - full width */}
          <div
            className="absolute h-px bg-onicorn-gray-500 z-40 w-screen"
            style={{
              top: 'calc(-1rem)',
              left: '50%',
              transform: 'translateX(-50%)'
            }}
          />
        </div>

        {/* Content Section - Responsive */}
        <div className="flex justify-center mt-8 md:mt-24 px-4 md:px-0">
          {/* Card with corner borders - Responsive */}
          <div
            className="flex-shrink-0 p-[1px] rounded-md relative z-10 w-full md:w-[890px]"
            style={{
              height: 'auto',
              minHeight: '460px',
              // Two separate corner gradients: top-left and bottom-right
              background: 'linear-gradient(225deg,transparent 50%,#8E8E8E 50%,#282828 100%),linear-gradient(45deg,transparent 50%,#8E8E8E 50%,#282828 100%)'
            }}
          >
            <div
              className="w-full h-full rounded-[7px] flex flex-col items-center justify-center relative py-8 md:py-0"
              style={{
                background: '#121212',
                boxShadow: '0 0 9.4px 0 rgba(255, 255, 255, 0.25) inset',
                minHeight: '460px'
              }}
            >
          {/* Card Title - Responsive */}
          <h2
            className="text-center mb-6 md:mb-8 text-[18px] md:text-[20px] px-4 md:px-0"
            style={{
              color: '#44FF02',
              fontFamily: 'Nunito Sans',
              fontStyle: 'normal',
              fontWeight: 700,
              lineHeight: '1.5'
            }}
          >
            Thank you for your submission!
          </h2>

          {/* Image Container with Glow Effect - Responsive */}
          <div className="relative mb-8 md:mb-12">
            {/* Image - Responsive size */}
            <img
              src="/icons/onboarding/submission.png"
              alt="Submission successful"
              className="relative z-10 w-[180px] md:w-auto h-auto"
            />

            {/* Glowing Shape Under Image - Responsive */}
            <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2">
              <div
                className=""
                style={{
                  width: '200px',
                  height: '56px',
                  borderRadius: '50%',
                  background: 'radial-gradient(ellipse at center, #44FF02 0%, rgba(68, 255, 2, 0.6) 30%, rgba(68, 255, 2, 0.2) 60%, transparent 100%)',
                  filter: 'blur(8px)',
                }}
              />
            </div>
          </div>

          {/* Description Text - Responsive */}
          <p className="text-center text-white max-w-lg px-6 md:px-0 text-[14px] md:text-[16px] leading-relaxed">
            The Onicorn team will review your application and respond within 48 hours.
            You may be contacted via Telegram for clarifications.
          </p>
            </div>
          </div>
        </div>

        {/* Back Button with top border */}
        <BackButton
          showTopBorder
          navigateTo="/"
        >
          Back to home
        </BackButton>
      </div>
    </div>
  );
}

// Export the service for use in form submission
export { SubmissionAccessService };

export const Route = createFileRoute('/project-submitted')({
  component: ProjectSubmittedPage,
});
